/**
 * Timezone Context
 *
 * Provides timezone cascade for the entire frontend:
 * 1. User timezone (highest priority)
 * 2. Team timezone
 * 3. Agency timezone (if exists)
 * 4. Default: Europe/Berlin (CEST/CET)
 *
 * Usage:
 * const { timezone, formatDate } = useTimezone();
 */

import React, { createContext, useContext, useMemo, useCallback, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import {
  DEFAULT_TIMEZONE,
  formatDate as formatDateUtil,
  formatDateTime,
  formatDateOnly,
  formatTimeOnly,
  formatRelativeDate,
  formatWithWeekday,
  formatForDateTimeInput,
  getTimezoneAbbreviation,
  isPastDate,
  isToday,
  fromTimezone,
  getHourInTimezone,
  DateFormatType,
} from '@/utils/dateFormat';

interface TimezoneContextType {
  /**
   * The resolved timezone based on cascade
   */
  timezone: string;

  /**
   * Source of the timezone (for debugging/display)
   */
  timezoneSource: 'user' | 'team' | 'agency' | 'default';

  /**
   * Timezone abbreviation (e.g., "CET", "CEST")
   */
  timezoneAbbreviation: string;

  /**
   * Format a date with the current timezone
   */
  formatDate: (date: string | Date | null | undefined, formatType?: DateFormatType) => string;

  /**
   * Format date and time (shorthand)
   */
  formatDateTime: (date: string | Date | null | undefined) => string;

  /**
   * Format date only (no time)
   */
  formatDateOnly: (date: string | Date | null | undefined) => string;

  /**
   * Format time only (no date)
   */
  formatTimeOnly: (date: string | Date | null | undefined) => string;

  /**
   * Format relative date (Today, Yesterday, etc.)
   */
  formatRelativeDate: (date: string | Date | null | undefined) => string;

  /**
   * Format with weekday
   */
  formatWithWeekday: (date: string | Date | null | undefined, short?: boolean) => string;

  /**
   * Format for datetime-local input
   */
  formatForInput: (date: string | Date | null | undefined) => string;

  /**
   * Check if date is in the past
   */
  isPastDate: (date: string | Date | null | undefined) => boolean;

  /**
   * Check if date is today
   */
  isToday: (date: string | Date | null | undefined) => boolean;

  /**
   * Convert a date from user's timezone to UTC
   * Use when user picks a time to save to database
   */
  toUTC: (date: Date | null | undefined) => Date | null;

  /**
   * Get the hour of a date in user's timezone
   * Use for grouping posts by time blocks
   */
  getHourInTimezone: (date: string | Date | null | undefined) => number | null;
}

const TimezoneContext = createContext<TimezoneContextType | null>(null);

interface TimezoneProviderProps {
  children: ReactNode;
  /**
   * Optional agency timezone (passed from agency context if exists)
   */
  agencyTimezone?: string | null;
}

export const TimezoneProvider: React.FC<TimezoneProviderProps> = ({
  children,
  agencyTimezone,
}) => {
  const { user } = useAuth();
  // Subscribing re-renders the provider (and its consumers) on language switch
  const { t, i18n: i18nInstance } = useTranslation('common');
  const language = i18nInstance.language;

  // Resolve timezone with cascade
  // Uses user.team from login response - no separate API call needed
  const { timezone, timezoneSource } = useMemo(() => {
    // 1. User timezone (highest priority)
    if (user?.timezone && user.timezone.trim()) {
      return { timezone: user.timezone, timezoneSource: 'user' as const };
    }

    // 2. Team timezone (from user.team in login response)
    if (user?.team?.timezone && user.team.timezone.trim()) {
      return { timezone: user.team.timezone, timezoneSource: 'team' as const };
    }

    // 3. Agency timezone (if provided)
    if (agencyTimezone && agencyTimezone.trim()) {
      return { timezone: agencyTimezone, timezoneSource: 'agency' as const };
    }

    // 4. Default timezone (Europe/Berlin for CEST)
    return { timezone: DEFAULT_TIMEZONE, timezoneSource: 'default' as const };
  }, [user?.timezone, user?.team?.timezone, agencyTimezone]);

  // Get timezone abbreviation
  const timezoneAbbreviation = useMemo(() => {
    return getTimezoneAbbreviation(timezone);
  }, [timezone]);

  // Memoized formatting functions
  const formatDateFn = useCallback(
    (date: string | Date | null | undefined, formatType: DateFormatType = 'full') => {
      return formatDateUtil(date, formatType, timezone);
    },
    [timezone]
  );

  const formatDateTimeFn = useCallback(
    (date: string | Date | null | undefined) => {
      return formatDateTime(date, timezone);
    },
    [timezone]
  );

  const formatDateOnlyFn = useCallback(
    (date: string | Date | null | undefined) => {
      return formatDateOnly(date, timezone);
    },
    [timezone]
  );

  const formatTimeOnlyFn = useCallback(
    (date: string | Date | null | undefined) => {
      return formatTimeOnly(date, timezone);
    },
    [timezone]
  );

  const formatRelativeDateFn = useCallback(
    (date: string | Date | null | undefined) => {
      return formatRelativeDate(date, timezone, t);
    },
    // language: new function identity on language switch so memoized consumers re-render
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [timezone, t, language]
  );

  const formatWithWeekdayFn = useCallback(
    (date: string | Date | null | undefined, short: boolean = false) => {
      return formatWithWeekday(date, timezone, short);
    },
    [timezone]
  );

  const formatForInputFn = useCallback(
    (date: string | Date | null | undefined) => {
      return formatForDateTimeInput(date, timezone);
    },
    [timezone]
  );

  const isPastDateFn = useCallback(
    (date: string | Date | null | undefined) => {
      return isPastDate(date, timezone);
    },
    [timezone]
  );

  const isTodayFn = useCallback(
    (date: string | Date | null | undefined) => {
      return isToday(date, timezone);
    },
    [timezone]
  );

  const toUTCFn = useCallback(
    (date: Date | null | undefined) => {
      return fromTimezone(date, timezone);
    },
    [timezone]
  );

  const getHourInTimezoneFn = useCallback(
    (date: string | Date | null | undefined) => {
      return getHourInTimezone(date, timezone);
    },
    [timezone]
  );

  const contextValue: TimezoneContextType = useMemo(
    () => ({
      timezone,
      timezoneSource,
      timezoneAbbreviation,
      formatDate: formatDateFn,
      formatDateTime: formatDateTimeFn,
      formatDateOnly: formatDateOnlyFn,
      formatTimeOnly: formatTimeOnlyFn,
      formatRelativeDate: formatRelativeDateFn,
      formatWithWeekday: formatWithWeekdayFn,
      formatForInput: formatForInputFn,
      isPastDate: isPastDateFn,
      isToday: isTodayFn,
      toUTC: toUTCFn,
      getHourInTimezone: getHourInTimezoneFn,
    }),
    [
      timezone,
      timezoneSource,
      timezoneAbbreviation,
      formatDateFn,
      formatDateTimeFn,
      formatDateOnlyFn,
      formatTimeOnlyFn,
      formatRelativeDateFn,
      formatWithWeekdayFn,
      formatForInputFn,
      isPastDateFn,
      isTodayFn,
      toUTCFn,
      getHourInTimezoneFn,
    ]
  );

  return (
    <TimezoneContext.Provider value={contextValue}>
      {children}
    </TimezoneContext.Provider>
  );
};

/**
 * Hook to use timezone context
 *
 * @example
 * const { formatDate, formatDateTime, timezone } = useTimezone();
 * return <span>{formatDateTime(post.scheduledAt)}</span>;
 */
export const useTimezone = (): TimezoneContextType => {
  const context = useContext(TimezoneContext);

  if (!context) {
    // Return default implementation if used outside provider
    // This allows the hook to work even without provider (with defaults)
    return {
      timezone: DEFAULT_TIMEZONE,
      timezoneSource: 'default',
      timezoneAbbreviation: getTimezoneAbbreviation(DEFAULT_TIMEZONE),
      formatDate: (date, formatType = 'full') => formatDateUtil(date, formatType, DEFAULT_TIMEZONE),
      formatDateTime: (date) => formatDateTime(date, DEFAULT_TIMEZONE),
      formatDateOnly: (date) => formatDateOnly(date, DEFAULT_TIMEZONE),
      formatTimeOnly: (date) => formatTimeOnly(date, DEFAULT_TIMEZONE),
      formatRelativeDate: (date) => formatRelativeDate(date, DEFAULT_TIMEZONE),
      formatWithWeekday: (date, short = false) => formatWithWeekday(date, DEFAULT_TIMEZONE, short),
      formatForInput: (date) => formatForDateTimeInput(date, DEFAULT_TIMEZONE),
      isPastDate: (date) => isPastDate(date, DEFAULT_TIMEZONE),
      isToday: (date) => isToday(date, DEFAULT_TIMEZONE),
      toUTC: (date) => fromTimezone(date, DEFAULT_TIMEZONE),
      getHourInTimezone: (date) => getHourInTimezone(date, DEFAULT_TIMEZONE),
    };
  }

  return context;
};

export default TimezoneContext;
